import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, User, Phone, Mail, FileText, Loader2, CheckCircle2, AlertTriangle, MessageCircle } from 'lucide-react';
import Reveal from '../components/Reveal';
import api from '../lib/api';
import { LEGAL } from '../lib/legal';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '919444150808';

const fields = [
  { name: 'name', label: 'Full Name', icon: User, type: 'text', required: true,
    hint: 'The name you used when you contacted us.' },
  { name: 'mobile', label: 'Mobile Number', icon: Phone, type: 'tel', required: true,
    hint: 'The number you used — this is how we find your records.' },
  { name: 'email', label: 'Email Address', icon: Mail, type: 'email', required: false,
    hint: 'Optional. Add it if you also gave us an email.' },
];

export default function DeleteData() {

  const [form, setForm] = useState({ name: '', mobile: '', email: '', reason: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) return;
    setStatus('sending');

    try {
      await api.post('/inquiries', {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        // Flagged clearly so the team never mistakes this for a sales lead.
        notes:
          '[DATA DELETION REQUEST]\n' +
          `Submitted via ${LEGAL.website}/delete\n` +
          `Reason / details: ${form.reason || '(none given)'}`,
        origin: 'contact',
        source: 'website',
      });
      setStatus('done');
    } catch (err) {
      // A deletion request must never fail silently — show the manual routes.
      console.error('Deletion request failed:', err?.message);
      setStatus('error');
    }
  };

  const mailtoHref =
    `mailto:${LEGAL.contactEmail}` +
    `?subject=${encodeURIComponent('Data deletion request')}` +
    `&body=${encodeURIComponent(
      `Please delete my data.\n\nName: ${form.name}\nMobile: ${form.mobile}\nEmail: ${form.email}\n\nDetails: ${form.reason}`
    )}`;

  const whatsappHref =
    `https://wa.me/${WHATSAPP_NUMBER}?text=` +
    encodeURIComponent(
      `Hello, I would like to request deletion of my data.\n\nName: ${form.name}\nMobile: ${form.mobile}`
    );

  return (
    <div className="section py-12">
      <Reveal className="mx-auto max-w-3xl text-center">
        <span className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-brand-gradient text-white">
          <Trash2 size={26} />
        </span>
        <h1 className="font-display text-4xl font-extrabold">
          Delete Your <span className="text-gradient">Data</span>
        </h1>
        <p className="mt-4 text-ink-300">
          Request deletion of the information {LEGAL.brand} holds about you. You do not need an
          account, and you do not need to install the app.
        </p>
      </Reveal>

      <div className="mx-auto mt-12 max-w-3xl space-y-6">
        <Reveal className="glass p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold">What Gets Deleted</h2>
          <div className="policy-prose mt-3">
            <p>
              {LEGAL.brand} has no customer sign-up, so there is no account to close. What we
              hold is the enquiry you sent us. On request we permanently delete:
            </p>
            <ul>
              <li>Your name, mobile number, and email address.</li>
              <li>The event date, location, and any notes you wrote.</li>
              <li>The packages and add-ons you selected, and the estimated total.</li>
              <li>The full enquiry record and its status history.</li>
            </ul>
          </div>
        </Reveal>

        <Reveal className="glass p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold">What We May Keep</h2>
          <div className="policy-prose mt-3">
            <ul>
              <li>
                Invoices, receipts, and tax records for events we actually delivered — we are
                legally required to retain these.
              </li>
              <li>
                Messages you sent us on <strong>WhatsApp</strong>. Those live in your WhatsApp
                chat as well as ours; to remove them fully, delete the chat on your device and
                ask us to delete it on ours.
              </li>
            </ul>
            <p>
              Everything else is removed within <strong>{LEGAL.deletionSlaDays} days</strong>, and
              we email or message you once it is done.
            </p>
          </div>
        </Reveal>

        <Reveal className="glass p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold">Request Deletion</h2>
          <p className="mt-2 text-sm text-ink-300">
            Give us the name and number you originally contacted us with, so we can find the
            right record. We may contact you to confirm the request is genuinely yours before we
            delete anything.
          </p>

          {status === 'done' ? (
            <div className="mt-6 rounded-xl border border-success/30 bg-success/10 p-5 text-center">
              <CheckCircle2 size={28} className="mx-auto text-success" />
              <p className="mt-3 font-semibold text-white">Request received</p>
              <p className="mt-1 text-sm text-ink-300">
                We will delete your data within {LEGAL.deletionSlaDays} days and confirm once it is
                done. Questions? Email{' '}
                <a href={`mailto:${LEGAL.contactEmail}`} className="text-brand hover:underline">
                  {LEGAL.contactEmail}
                </a>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.map((f) => (
                  <label key={f.name} className={`block ${f.name === 'email' ? 'sm:col-span-2' : ''}`}>
                    <span className="mb-1.5 flex items-center gap-1.5 text-sm text-ink-300">
                      <f.icon size={14} /> {f.label}{' '}
                      {f.required && <span className="text-danger">*</span>}
                    </span>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.name]}
                      onChange={update(f.name)}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-brand focus:bg-white/10"
                    />
                    <span className="mt-1 block text-xs text-ink-500">{f.hint}</span>
                  </label>
                ))}
              </div>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 text-sm text-ink-300">
                  <FileText size={14} /> Anything Else (optional)
                </span>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={update('reason')}
                  placeholder="e.g. which enquiry this relates to"
                  className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-brand focus:bg-white/10 placeholder:text-ink-500"
                />
              </label>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="btn-primary w-full disabled:opacity-60"
              >
                {status === 'sending' ? (
                  <><Loader2 size={18} className="animate-spin" /> Sending…</>
                ) : (
                  <><Trash2 size={18} /> Submit Deletion Request</>
                )}
              </button>

              {status === 'error' && (
                <div className="rounded-xl border border-danger/30 bg-danger/10 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-white">
                    <AlertTriangle size={16} className="text-danger" /> We could not submit that
                    automatically
                  </p>
                  <p className="mt-1 text-sm text-ink-300">
                    Please send your request directly — both routes reach the same team:
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <a href={mailtoHref} className="btn-ghost">
                      <Mail size={16} /> Email Us
                    </a>
                    <a href={whatsappHref} target="_blank" rel="noreferrer" className="btn-ghost">
                      <MessageCircle size={16} /> WhatsApp
                    </a>
                  </div>
                </div>
              )}
            </form>
          )}
        </Reveal>

        <Reveal className="glass p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold">Prefer to Contact Us Directly?</h2>
          <div className="policy-prose mt-3">
            <ul>
              <li>
                Email:{' '}
                <a href={`mailto:${LEGAL.contactEmail}`} className="text-brand hover:underline">
                  {LEGAL.contactEmail}
                </a>{' '}
                with the subject “Data deletion request”.
              </li>
              <li>Phone / WhatsApp: {LEGAL.phone}</li>
            </ul>
            <p>
              See our <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link>{' '}
              for what we collect and why.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
